import { useNavigate } from "react-router-dom";
import { PATH_INFO } from "../../util/helper";
import { useCallback, useContext, useEffect, useRef } from "react";
import { App, Badge, Button, Divider, Flex, Space, Table } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import Column from "antd/es/table/Column";
import { ExclamationCircleFilled, PlusSquareOutlined } from "@ant-design/icons";
import { CompanyContext, CompanyResponseType, UpdateCompanyContext } from "../../../context/companyContext";

const DEFAULT_PAGE_SIZE = 8

export function CompanyList() {
  const { companies, companyPagination } = useContext(CompanyContext)
  const { deleteCompany, toggleCompanyStatus, refreshCompanies } = useContext(UpdateCompanyContext)

  const navigate = useNavigate()
  const { modal } = App.useApp()
  const didMountRef = useRef(false)

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true

      if (companyPagination.limit !== DEFAULT_PAGE_SIZE || companyPagination.page !== 1) {
        refreshCompanies({ page: 1, limit: DEFAULT_PAGE_SIZE })
      }
      return
    }
  }, [companyPagination.limit, companyPagination.page, refreshCompanies])

  const onClickTitle = useCallback((id: number) => {
    const result = PATH_INFO.COMPANY_INFO.PATH.replace(":companyId", String(id))
    navigate(result)
  }, [navigate]);

  const onClickManage = useCallback((id: number) => {
    const result = PATH_INFO.COMPANY_MANAGE.PATH.replace(":companyId", String(id))
    navigate(result)
  }, [navigate]);

  const onClickToggleStatus = useCallback((id: number) => {
    toggleCompanyStatus(id)
  }, [toggleCompanyStatus])

  const onClickRegister = useCallback(() => {
    const result = PATH_INFO.COMPANY_MANAGE.PATH.replace(":companyId", "register")
    navigate(result)
  }, [navigate])

  const getOnCell = (record: CompanyResponseType) => ({
    onClick: () => onClickTitle(record.id),
    style: {
      background: record.is_disabled ? 'rgba(0, 0, 0, 0.04)' : '',
      color: record.is_disabled ? 'rgba(0,0,0,0.5)' : '',
      cursor: "pointer"
    }
  })

  const formatNumber = useCallback((num: string) => {
    if (!num) {
      return '-'
    }

    if (num.length === 11) {
      return `${num.slice(0, 3)}-${num.slice(3, 7)}-${num.slice(7)}`
    } else if (num.length === 10) {
      return `${num.slice(0, 4)}-${num.slice(4, 7)}-${num.slice(7)}`
    } else {
      return num
    }
  }, [])

  const handleTableChange = useCallback((pagination: TablePaginationConfig) => {
    const nextPage = pagination.current ?? 1
    const nextPageSize = pagination.pageSize ?? companyPagination.limit

    if (nextPage === companyPagination.page && nextPageSize === companyPagination.limit) {
      return
    }

    refreshCompanies({ page: nextPage, limit: nextPageSize })
  }, [companyPagination.limit, companyPagination.page, refreshCompanies])

  return (
    <div>
      <div className="flex text-2xl decoration-solid ml-2 border-0 border-l-4 border-solid border-[#1677ff] p-2">
        企業一覧画面
      </div>
      <Divider />
      <div className="mb-1 pr-5 flex justify-end w-full">
        <Badge status="processing" color="blue" />
        <span className="pl-1">企業合計数: {companyPagination.total}</span>
      </div>
      <Flex vertical justify="center" className="relative">
        <Table
          dataSource={companies}
          bordered
          pagination={{
            current: companyPagination.page,
            pageSize: companyPagination.limit,
            total: companyPagination.total,
            position: ['bottomCenter'],
          }}
          onChange={handleTableChange}
          rowKey={(record) => record.id}
          scroll={{ x: 'max-content' }}
        >
          <Column
            title='企業名'
            dataIndex="company_name"
            key="company_name"
            onCell={getOnCell}
            render={(text: string) => text ? text : '-'}
          />
          <Column
            title='住所'
            dataIndex='address'
            key='address'
            onCell={getOnCell}
            render={(text: string) => text ? text : '-'}
          />
          <Column
            title='電話番号'
            dataIndex='phone_number'
            key='phone_number'
            onCell={getOnCell}
            render={(phone_number: string) => formatNumber(phone_number)}
          />
          <Column
            title='操作'
            dataIndex='action'
            key='action'
            fixed='right'
            render={(_: any, record: CompanyResponseType) => (
              <Space size='middle'>
                <Button variant='outlined' color='primary' onClick={() => onClickManage(record.id)}>編集</Button>
                <Button variant='outlined' color={record.is_disabled ? 'green' : 'danger'} onClick={() => onClickToggleStatus(record.id)}>{record.is_disabled ? '復活' : '停止'}</Button>
                <Button
                  variant='outlined'
                  color='danger'
                  onClick={() => {
                    modal.warning({
                      title: `「${record.company_name}」を削除します。`,
                      icon: <ExclamationCircleFilled />,
                      content: 'この操作は取り消せません。よろしいですか？',
                      centered: true,
                      cancelText: 'Cancel',
                      okText: 'Yes',
                      okType: 'danger',
                      okCancel: true,
                      onOk() { deleteCompany(record.id) },
                      onCancel() { }
                    })
                  }}>
                  削除
                </Button>
              </Space>
            )}
            width='255px'
          />
        </Table>
      </Flex>
      <Flex className="justify-end">
        <Button type="primary" onClick={onClickRegister} icon={<PlusSquareOutlined />}>企業・部署を登録</Button>
      </Flex>
    </div>
  )
}
